/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bot, Search, X, Building2, AlertTriangle, Filter, Activity, Layers, BookOpen, Cpu, Trash2, PlusCircle
} from 'lucide-react';
import { aiAgentService } from '../services/aiAgentService';
import { AI_AGENT_ERROR_CODES, getApiErrorMessage } from '../services/knowledgeBaseService';
import { accountService } from '../services/accountService';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { getAiAgentProviderLabel, getAiAgentTypeLabel } from '../constants/aiAgents';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import AgenticAiUnavailable from '../components/aiAgents/AgenticAiUnavailable';
import { getAgenticAiServiceError } from '../utils/agenticAiErrors';
import { parseTimestamp, formatDate, formatDateTime } from '../utils/agenticAiDates';
import { ACCOUNT_LABEL } from '../constants/entityLabels';

const INITIAL_FILTERS = {
  search: '',
  type: 'all',
  status: 'all', // 'all' | 'live' | 'not_live'
};

const STATUS_LABELS = { live: 'Live', not_live: 'Not Live' };

const AiAgents = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const canCreateAgent = can.ai_agents.create();
  const canDeleteAgent = can.ai_agents.delete();
  const canViewKnowledgeBases = can.knowledge_bases.view();
  const [searchParams, setSearchParams] = useSearchParams();

  // Platform-wide view picks the account; account / sub-account users are pinned to their own.
  const showAccountField = !user?.accountUID;
  const requestedAccountId = searchParams.get('accountId') || '';

  const [accounts, setAccounts] = useState([]);
  // 'loading' | 'loaded' | 'failed' — the URL account is only trusted once it's known to exist.
  const [accountsStatus, setAccountsStatus] = useState('loading');
  const accountsLoading = showAccountField && accountsStatus === 'loading';
  const isKnownAccount = accountsStatus === 'failed' || accounts.some((a) => a.id === requestedAccountId);
  let selectedAccountId = user?.accountUID;
  if (showAccountField) {
    selectedAccountId = !accountsLoading && requestedAccountId && isKnownAccount ? requestedAccountId : '';
  }
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [integrationError, setIntegrationError] = useState(null);
  // Set when the Agentic AI platform itself does not respond properly.
  const [serviceError, setServiceError] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!showAccountField) { return; }
    const fetchAccounts = async () => {
      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        setAccounts([...(data.data || data || [])].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })));
        setAccountsStatus('loaded');
      } catch {
        setAccountsStatus('failed');
        toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchAccounts();
  }, [showAccountField]);

  const handleAccountChange = (accountId) => {
    setFilters(INITIAL_FILTERS);
    setSearchParams(accountId ? { accountId } : {}, { replace: true });
  };

  const requestIdRef = useRef(0);
  const fetchAgents = async () => {
    const currentRequestId = ++requestIdRef.current;
    setIntegrationError(null);
    setServiceError(null);
    if (!selectedAccountId) {
      setAgents([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await aiAgentService.getAiAgents(
        showAccountField ? { accountId: selectedAccountId } : {}
      );
      if (currentRequestId !== requestIdRef.current) { return; }
      setAgents(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) { return; }
      setAgents([]);
      const message = getApiErrorMessage(err, 'Failed to load AI agents');
      if (getAgenticAiServiceError(err)) {
        setServiceError(getAgenticAiServiceError(err));
      } else if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
        setIntegrationError(message);
      } else if (showAccountField && err.response?.status === 404) {
        // Account no longer exists — same as no account selected.
        handleAccountChange('');
      } else {
        toastService.error(message);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  // An unknown ?accountId= is treated as "no account selected" (dropped from the URL, no error).
  useEffect(() => {
    if (showAccountField && accountsStatus === 'loaded' && requestedAccountId && !isKnownAccount) {
      setSearchParams({}, { replace: true });
    }
  }, [showAccountField, accountsStatus, requestedAccountId, isKnownAccount, setSearchParams]);

  const typeOptions = useMemo(
    () => [...new Set(agents.map((a) => a.type).filter(Boolean))].sort(),
    [agents]
  );

  const filteredAgents = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return agents.filter((agent) => {
      if (filters.type !== 'all' && agent.type !== filters.type) { return false; }
      if (filters.status === 'live' && !agent.isLive) { return false; }
      if (filters.status === 'not_live' && agent.isLive) { return false; }
      if (!term) { return true; }
      const haystack = [
        agent.name, agent.role, agent.provider, agent.model,
        ...(agent.knowledgeBases || []).map((kb) => kb.name),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [agents, filters]);

  const needsAccount = showAccountField && !selectedAccountId;
  const selectedAccountName = accounts.find(a => a.id === selectedAccountId)?.name || selectedAccountId;
  const hasListFilters = Boolean(filters.search || filters.type !== 'all' || filters.status !== 'all');
  const hasActiveFilters = hasListFilters || Boolean(showAccountField && selectedAccountId);
  const filtersDisabled = needsAccount || Boolean(integrationError) || Boolean(serviceError);

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    if (showAccountField) { handleAccountChange(''); }
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await aiAgentService.deleteAiAgent(
        deleteTarget.id,
        showAccountField ? { accountId: selectedAccountId } : {}
      );
      toastService.success('AI agent deleted successfully');
      setDeleteTarget(null);
      fetchAgents();
    } catch (err) {
      toastService.error(getApiErrorMessage(err, 'Failed to delete AI agent'));
    } finally {
      setDeleting(false);
    }
  };

  const canCreate = Boolean(selectedAccountId) && !integrationError && !serviceError;
  const handleNew = () => {
    const query = showAccountField && selectedAccountId ? `?accountId=${encodeURIComponent(selectedAccountId)}` : '';
    navigate(`/ai-agents/new${query}`);
  };

  const openAgent = (agent) => {
    const query = showAccountField ? `?accountId=${encodeURIComponent(selectedAccountId)}` : '';
    navigate(`/ai-agents/${encodeURIComponent(agent.id)}${query}`, {
      state: { agent: { id: agent.id, name: agent.name } },
    });
  };

  const openKnowledgeBase = (kb) => {
    const query = showAccountField ? `?accountId=${encodeURIComponent(selectedAccountId)}` : '';
    navigate(`/knowledge-bases/${encodeURIComponent(kb.id)}/documents${query}`, {
      state: { knowledgeBase: { id: kb.id, name: kb.name } },
    });
  };

  const renderEmptyState = (icon, title, description, action = null) => (
    <div className="py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
      <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
        {icon}
      </div>
      <div>
        <h3 className="text-base font-black text-base-content">{title}</h3>
        <p className="text-xs text-base-content/40 max-w-sm mt-1">{description}</p>
      </div>
      {action}
    </div>
  );

  const renderKnowledgeBases = (agent) => {
    const kbs = agent.knowledgeBases || [];
    if (kbs.length === 0) { return <span className="text-base-content/40">—</span>; }
    return (
      <div className="flex flex-wrap gap-1.5 max-w-xs">
        {kbs.map((kb) => (kb.name ? (
          canViewKnowledgeBases ? (
            <button
              key={kb.id}
              type="button"
              onClick={() => openKnowledgeBase(kb)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors"
            >
              <BookOpen size={10} />
              <span>{kb.name}</span>
            </button>
          ) : (
            <span
              key={kb.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-primary/10 text-primary border-primary/20"
            >
              <BookOpen size={10} />
              <span>{kb.name}</span>
            </span>
          )
        ) : (
          <span
            key={kb.id}
            className="tooltip tooltip-top before:text-xs before:font-mono before:whitespace-nowrap"
            data-tip={kb.id}
          >
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-base-200 text-base-content/50 border-base-content/5">
              <BookOpen size={10} />
              <span>Unknown knowledge base</span>
            </span>
          </span>
        )))}
      </div>
    );
  };

  const renderBody = () => {
    if (needsAccount && !(accountsLoading && requestedAccountId)) {
      return renderEmptyState(
        <Building2 size={28} />,
        `Select ${ACCOUNT_LABEL.withArticle}`,
        `Choose ${ACCOUNT_LABEL.withArticle} to view its AI agents.`
      );
    }
    if (loading || accountsLoading) {
      return (
        <div className="p-6 space-y-4">
          <div className="h-6 w-32 bg-base-300/60 rounded animate-pulse"></div>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 bg-base-200/50 rounded-xl animate-pulse"></div>
            ))}
          </div>
        </div>
      );
    }
    if (serviceError) {
      return <AgenticAiUnavailable bare error={serviceError} onRetry={() => fetchAgents()} />;
    }
    if (integrationError) {
      return (
        <div className="p-5 sm:p-6">
          <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
            <AlertTriangle size={18} className="shrink-0" />
            <span>{integrationError}</span>
          </div>
        </div>
      );
    }
    if (filteredAgents.length === 0) {
      return hasListFilters
        ? renderEmptyState(
          <Bot size={28} />,
          'No matching AI agents',
          'Try adjusting your search query or reset filters.',
          <button
            type="button"
            onClick={handleClearFilters}
            className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
          >
            Clear all filters
          </button>
        )
        : renderEmptyState(
          <Bot size={28} />,
          'No AI agents found',
          canCreateAgent
            ? `Get started by creating the first AI agent for this ${ACCOUNT_LABEL.singularLower}.`
            : `This ${ACCOUNT_LABEL.singularLower} has no AI agents yet.`,
          canCreateAgent ? (
            <button
              type="button"
              onClick={handleNew}
              className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
            >
              <PlusCircle size={14} />
              <span>Create AI Agent</span>
            </button>
          ) : null
        );
    }
    return (
      <div className="overflow-x-auto">
        <table className="table w-full text-left">
          <thead>
            <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
              <th className="py-3.5 px-5">Name</th>
              <th className="py-3.5 px-4">Role</th>
              <th className="py-3.5 px-4">Type</th>
              <th className="py-3.5 px-4">Model</th>
              <th className="py-3.5 px-4">Knowledge Bases</th>
              <th className="py-3.5 px-4 text-center">Status</th>
              <th className="py-3.5 px-4">Created Date</th>
              {canDeleteAgent && <th className="py-3.5 px-5 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-base-content/5 text-xs">
            {filteredAgents.map((agent) => (
              <tr key={agent.id} className="hover:bg-base-200/40 transition-colors align-top">
                {/* Name (opens the detail page) */}
                <td className="py-3.5 px-5 min-w-[12rem]">
                  <button
                    type="button"
                    onClick={() => openAgent(agent)}
                    className="text-primary hover:underline font-bold text-sm tracking-tight text-left break-words"
                  >
                    {agent.name}
                  </button>
                </td>

                {/* Role */}
                <td className="py-3.5 px-4">
                  <span className="text-base-content/70 font-medium">{agent.role || '—'}</span>
                </td>

                {/* Type */}
                <td className="py-3.5 px-4 whitespace-nowrap">
                  {agent.type ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                      {getAiAgentTypeLabel(agent.type)}
                    </span>
                  ) : (
                    <span className="text-base-content/40">—</span>
                  )}
                </td>

                {/* Model: model + provider */}
                <td className="py-3.5 px-4 whitespace-nowrap">
                  {agent.model || agent.provider ? (
                    <div>
                      <p className="font-mono text-base-content/80 font-medium">{agent.model || '—'}</p>
                      {agent.provider && (
                        <p className="text-[11px] text-base-content/50 font-medium">{getAiAgentProviderLabel(agent.provider)}</p>
                      )}
                    </div>
                  ) : (
                    <span className="text-base-content/40">—</span>
                  )}
                </td>

                {/* Knowledge bases */}
                <td className="py-3.5 px-4">{renderKnowledgeBases(agent)}</td>

                {/* Status */}
                <td className="py-3.5 px-4 text-center">
                  <StatusBadge status={agent.isLive ? 'live' : 'not_live'} />
                </td>

                {/* Created date */}
                <td className="py-3.5 px-4 text-base-content/70 font-medium whitespace-nowrap">
                  {parseTimestamp(agent.createdAt) ? (
                    <div
                      className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                      data-tip={formatDateTime(agent.createdAt)}
                    >
                      <span className="cursor-default hover:text-base-content transition-colors">
                        {formatDate(agent.createdAt)}
                      </span>
                    </div>
                  ) : (
                    '—'
                  )}
                </td>

                {/* Actions */}
                {canDeleteAgent && (
                  <td className="py-3.5 px-5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <div className="tooltip tooltip-left tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Delete">
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(agent)}
                          className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                          aria-label="Delete AI Agent"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const selectClassName = 'w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium disabled:opacity-50';
  const chipClassName = 'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5';

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              AI Agents
            </h1>
            <p className="text-xs text-base-content/50">
              AI agents configured for the {ACCOUNT_LABEL.singularLower} on the Agentic AI platform.
            </p>
          </div>

          {canCreateAgent && (
            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                type="button"
                onClick={handleNew}
                disabled={!canCreate}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <PlusCircle size={15} />
                <span>New AI Agent</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        <div className="grid grid-cols-12 gap-3">
          {/* Search Input */}
          <div className={`relative col-span-12 ${showAccountField ? 'lg:col-span-5' : 'lg:col-span-8'}`}>
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={16} />
            <input
              type="text"
              placeholder="Search by name, role, model or knowledge base..."
              disabled={filtersDisabled}
              className="w-full pl-10 pr-9 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilters((f) => ({ ...f, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Type Filter */}
          <div className="col-span-6 sm:col-span-4 lg:col-span-2">
            <select
              aria-label="Type"
              disabled={filtersDisabled}
              className={selectClassName}
              value={filters.type}
              onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
            >
              <option value="all">All Types</option>
              {typeOptions.map((t) => (
                <option key={t} value={t}>{getAiAgentTypeLabel(t)}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="col-span-6 sm:col-span-4 lg:col-span-2">
            <select
              aria-label="Status"
              disabled={filtersDisabled}
              className={selectClassName}
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="all">All Statuses</option>
              <option value="live">Live</option>
              <option value="not_live">Not Live</option>
            </select>
          </div>

          {/* Account Filter (platform-wide view only) */}
          {showAccountField && (
            <div className="col-span-12 sm:col-span-4 lg:col-span-3">
              <select
                aria-label={ACCOUNT_LABEL.singular}
                className={selectClassName}
                value={selectedAccountId}
                onChange={(e) => handleAccountChange(e.target.value)}
              >
                <option value="">— Select {ACCOUNT_LABEL.withArticle} —</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {filters.search && (
                <span className={chipClassName}>
                  <span>Search: "{filters.search}"</span>
                  <button type="button" onClick={() => setFilters((f) => ({ ...f, search: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.type !== 'all' && (
                <span className={chipClassName}>
                  <Layers size={10} className="text-primary" />
                  <span>Type: {getAiAgentTypeLabel(filters.type)}</span>
                  <button type="button" onClick={() => setFilters((f) => ({ ...f, type: 'all' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.status !== 'all' && (
                <span className={chipClassName}>
                  <Activity size={10} className="text-primary" />
                  <span>Status: {STATUS_LABELS[filters.status]}</span>
                  <button type="button" onClick={() => setFilters((f) => ({ ...f, status: 'all' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {showAccountField && selectedAccountId && (
                <span className={chipClassName}>
                  <Building2 size={10} className="text-primary" />
                  <span>{ACCOUNT_LABEL.singular}: {selectedAccountName}</span>
                  <button type="button" onClick={() => handleAccountChange('')} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main Table Card */}
      <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative z-10">
        {renderBody()}

        {!loading && !integrationError && !serviceError && filteredAgents.length > 0 && (
          <div className="px-4 py-2.5 border-t border-base-content/5 flex items-center justify-between gap-3 flex-wrap text-xs text-base-content/60 font-medium">
            <span>
              <span className="font-semibold text-base-content">{filteredAgents.length}</span>
              {hasListFilters ? <> of <span className="font-semibold text-base-content">{agents.length}</span></> : null} AI agents
            </span>
            <span className="inline-flex items-center gap-1.5 text-base-content/50">
              <Cpu size={12} />
              {agents.filter((a) => a.isLive).length} live
            </span>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => { if (!deleting) { setDeleteTarget(null); } }}
        title="Delete AI Agent"
      >
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to delete{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200 break-all">
              {deleteTarget?.name}
            </strong>?
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            This permanently removes the agent and its configuration from the Agentic AI platform. This action cannot be undone.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : 'Delete AI Agent'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AiAgents;
